import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Chip,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { projectService } from '../../services/projectService';
import { sowService } from '../../services/sowService';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SettingsIcon from '@mui/icons-material/Settings';
import ProjectBillingConfig from './ProjectBillingConfig';
import ConfirmDialog from '../Common/ConfirmDialog';
import { format, parseISO } from 'date-fns';

const ProjectEdit = () => {
  const { code } = useParams();
  const navigate = useNavigate();

  // Project state
  const [project, setProject] = useState(null);
  const [projectForm, setProjectForm] = useState({
    name: '',
    description: '',
    status: 'Active',
    baseline_burn_value: '',
    baseline_month: '',
  });

  // SOW state
  const [sowMetrics, setSowMetrics] = useState(null);
  const [sowValues, setSowValues] = useState([]);
  const [sowDialogOpen, setSowDialogOpen] = useState(false);
  const [editingSow, setEditingSow] = useState(null);
  const [sowForm, setSowForm] = useState({
    sow_value: '',
    effective_date: '',
    end_date: '',
    description: '',
  });
  const [dialogError, setDialogError] = useState('');

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [savingProject, setSavingProject] = useState(false);
  const [savingSow, setSavingSow] = useState(false);
  const [showBillingConfig, setShowBillingConfig] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sowToDelete, setSowToDelete] = useState(null);

  useEffect(() => {
    loadProjectData();
  }, [code]);

  const loadProjectData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load project details
      const projectData = await projectService.getByCode(code);
      setProject(projectData);

      // Convert baseline_month from YYYY-MM-01 to YYYY-MM for month input
      const baselineMonthForInput = projectData.baseline_month
        ? projectData.baseline_month.substring(0, 7)
        : '';

      setProjectForm({
        name: projectData.name || '',
        description: projectData.description || '',
        status: projectData.status || 'Active',
        baseline_burn_value: projectData.baseline_burn_value || '',
        baseline_month: baselineMonthForInput,
      });

      // Load SOW metrics and values
      await loadSOWData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load project data');
    } finally {
      setLoading(false);
    }
  };

  const loadSOWData = async () => {
    try {
      const metricsData = await sowService.getSOWMetrics(code);
      setSowMetrics(metricsData.metrics);
      setSowValues(metricsData.metrics.sow_history || []);
    } catch (err) {
      console.error('Failed to load SOW data:', err);
    }
  };

  const handleProjectChange = (e) => {
    const { name, value } = e.target;
    setProjectForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveProject = async () => {
    try {
      setSavingProject(true);
      setError('');

      // Format baseline month to YYYY-MM-01 if provided
      const formattedBaselineMonth = projectForm.baseline_month
        ? `${projectForm.baseline_month}-01`
        : null;

      // Parse baseline burn value
      const baselineBurnValue = projectForm.baseline_burn_value && projectForm.baseline_burn_value !== ''
        ? parseFloat(projectForm.baseline_burn_value)
        : 0;

      const updateData = {
        name: projectForm.name,
        description: projectForm.description,
        status: projectForm.status,
        baseline_burn_value: baselineBurnValue,
        baseline_month: formattedBaselineMonth,
      };

      await projectService.update(code, updateData);
      setSuccess('Project updated successfully');
      setTimeout(() => setSuccess(''), 3000);

      // Reload project data and SOW metrics
      const projectData = await projectService.getByCode(code);
      setProject(projectData);
      await loadSOWData(); // Refresh SOW metrics to show updated baseline
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update project');
    } finally {
      setSavingProject(false);
    }
  };

  const handleOpenSowDialog = (sow = null) => {
    setDialogError('');
    if (sow) {
      // Edit mode - only description, effective_date, and end_date can be edited
      setEditingSow(sow);
      setSowForm({
        sow_value: sow.sow_value,
        effective_date: sow.effective_date,
        end_date: sow.end_date || '',
        description: sow.description || '',
      });
    } else {
      // Create mode
      setEditingSow(null);
      setSowForm({
        sow_value: '',
        effective_date: new Date().toISOString().split('T')[0],
        end_date: '',
        description: '',
      });
    }
    setSowDialogOpen(true);
  };

  const handleCloseSowDialog = () => {
    setSowDialogOpen(false);
    setEditingSow(null);
    setSowForm({
      sow_value: '',
      effective_date: '',
      end_date: '',
      description: '',
    });
  };

  const handleSowFormChange = (e) => {
    const { name, value } = e.target;
    setSowForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveSow = async () => {
    try {
      setSavingSow(true);
      setDialogError('');

      if (editingSow) {
        // Update existing SOW (only description, effective_date, and end_date)
        await sowService.updateSOWValue(editingSow.id, {
          description: sowForm.description,
          effective_date: sowForm.effective_date,
          end_date: sowForm.end_date || null,
        });
        setSuccess('SOW value updated successfully');
      } else {
        // Create new SOW
        if (!sowForm.sow_value || parseFloat(sowForm.sow_value) < 0) {
          setDialogError('SOW value must be a positive number');
          setSavingSow(false);
          return;
        }

        const sowData = {
          project_code: code,
          sow_value: parseFloat(sowForm.sow_value),
          effective_date: sowForm.effective_date,
          end_date: sowForm.end_date || null,
          description: sowForm.description,
        };

        await sowService.createSOWValue(sowData);
        setSuccess('SOW value created successfully');
      }

      handleCloseSowDialog();

      // Refresh SOW data
      await loadSOWData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setDialogError(err.response?.data?.error || 'Failed to save SOW value');
    } finally {
      setSavingSow(false);
    }
  };

  const handleDeleteClick = (sow) => {
    setSowToDelete(sow);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!sowToDelete) return;
    try {
      setError('');
      await sowService.deleteSOWValue(sowToDelete.id);
      setSuccess('SOW value deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
      await loadSOWData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete SOW value');
    } finally {
      setDeleteDialogOpen(false);
      setSowToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSowToDelete(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!project) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error">Project not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/projects')}
          sx={{ mb: 2 }}
        >
          Back to Projects
        </Button>
        <Typography variant="h4">Edit Project: {project.project_code}</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Project Details Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Project Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Project Code"
              value={project.project_code}
              disabled
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Project Name"
              name="name"
              value={projectForm.name}
              onChange={handleProjectChange}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={projectForm.description}
              onChange={handleProjectChange}
              variant="outlined"
              multiline
              rows={3}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Project Start Date (From SOW)"
              value={project?.sow_start_date || 'Not set'}
              variant="outlined"
              disabled
              InputLabelProps={{ shrink: true }}
              helperText="Auto-calculated from first SOW amendment"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Project End Date (From SOW)"
              value={project?.sow_end_date || 'Ongoing'}
              variant="outlined"
              disabled
              InputLabelProps={{ shrink: true }}
              helperText="Auto-calculated from last SOW amendment"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              select
              label="Status"
              name="status"
              value={projectForm.status}
              onChange={handleProjectChange}
              variant="outlined"
              SelectProps={{ native: true }}
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Baseline Burn Value"
              name="baseline_burn_value"
              type="number"
              value={projectForm.baseline_burn_value}
              onChange={handleProjectChange}
              variant="outlined"
              helperText="Cumulative burn value from project start till baseline month"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Baseline Month"
              name="baseline_month"
              type="month"
              value={projectForm.baseline_month}
              onChange={handleProjectChange}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              helperText="Month till which baseline burn value is calculated"
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveProject}
              disabled={savingProject}
            >
              {savingProject ? 'Saving...' : 'Save Project'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* SOW Metrics Section */}
      {sowMetrics && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            SOW Metrics
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom fontSize="0.875rem">
                    Total SOW Value
                  </Typography>
                  <Typography variant="h6" color="primary">
                    ${sowMetrics.total_sow_value?.toLocaleString() || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom fontSize="0.875rem">
                    Baseline Burn
                  </Typography>
                  <Typography variant="h6" color="warning.main">
                    ${sowMetrics.baseline_burn_value?.toLocaleString() || 0}
                  </Typography>
                  {sowMetrics.baseline_month && (
                    <Typography variant="caption" color="textSecondary">
                      till {new Date(sowMetrics.baseline_month).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom fontSize="0.875rem">
                    Total Burn Value
                  </Typography>
                  <Typography variant="h6" color="error">
                    ${sowMetrics.burn_value?.toLocaleString() || 0}
                  </Typography>
                  {sowMetrics.burn_value_till_month && (
                    <Typography variant="caption" color="textSecondary">
                      till {new Date(sowMetrics.burn_value_till_month).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom fontSize="0.875rem">
                    Remaining SOW
                  </Typography>
                  <Typography
                    variant="h6"
                    color={sowMetrics.remaining_sow_value >= 0 ? 'success.main' : 'error'}
                  >
                    ${sowMetrics.remaining_sow_value?.toLocaleString() || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom fontSize="0.875rem">
                    Burn %
                  </Typography>
                  <Typography variant="h6" color="textPrimary">
                    {sowMetrics.burn_percentage?.toFixed(2) || 0}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom fontSize="0.875rem">
                    SOW Versions
                  </Typography>
                  <Typography variant="h6" color="textPrimary">
                    {sowMetrics.sow_history?.length || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* SOW Values Section */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">SOW Values & Amendments</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenSowDialog()}
          >
            Add SOW Value
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Version</TableCell>
                <TableCell>SOW Value</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sowValues.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No SOW values found. Add the first SOW value to get started.
                  </TableCell>
                </TableRow>
              ) : (
                sowValues.map((sow) => (
                  <TableRow key={sow.id}>
                    <TableCell>
                      <Chip label={`v${sow.version}`} color="primary" size="small" />
                    </TableCell>
                    <TableCell>
                      <strong>${sow.sow_value?.toLocaleString()}</strong>
                    </TableCell>
                    <TableCell>{sow.effective_date}</TableCell>
                    <TableCell>{sow.end_date || 'Ongoing'}</TableCell>
                    <TableCell>{sow.description || '-'}</TableCell>
                    <TableCell>{sow.created_by || '-'}</TableCell>
                    <TableCell>{new Date(sow.created_at).toLocaleString()}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenSowDialog(sow)}
                        size="small"
                        sx={{ mr: 1 }}
                        title="Edit description/date"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteClick(sow)}
                        size="small"
                        title="Delete SOW value"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Billing Configuration Section */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Billing Configuration</Typography>
          <Button
            variant={showBillingConfig ? 'outlined' : 'contained'}
            color="primary"
            startIcon={<SettingsIcon />}
            onClick={() => setShowBillingConfig(!showBillingConfig)}
          >
            {showBillingConfig ? 'Hide Configuration' : 'Manage Billing Config'}
          </Button>
        </Box>

        {showBillingConfig ? (
          <ProjectBillingConfig projectCode={code} />
        ) : (
          <Alert severity="info">
            Click "Manage Billing Config" to configure discount percentage, minimum billable days,
            and maximum billable days with date-ranged values for this project.
          </Alert>
        )}
      </Paper>

      {/* SOW Dialog */}
      <Dialog open={sowDialogOpen} onClose={handleCloseSowDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingSow ? 'Edit SOW Value' : 'Add SOW Value'}</DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mt: 1, mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          <Box sx={{ pt: dialogError ? 0 : 2 }}>
            <TextField
              fullWidth
              label="SOW Value"
              name="sow_value"
              type="number"
              value={sowForm.sow_value}
              onChange={handleSowFormChange}
              disabled={editingSow !== null}
              sx={{ mb: 2 }}
              helperText={editingSow ? 'SOW value cannot be changed after creation' : 'Enter a positive number'}
            />
            <TextField
              fullWidth
              label="Start Date (Effective Date)"
              name="effective_date"
              type="date"
              value={sowForm.effective_date}
              onChange={handleSowFormChange}
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
              helperText="When this SOW amendment becomes effective"
            />
            <TextField
              fullWidth
              label="End Date (Optional)"
              name="end_date"
              type="date"
              value={sowForm.end_date}
              onChange={handleSowFormChange}
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty if the amendment is ongoing"
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={sowForm.description}
              onChange={handleSowFormChange}
              multiline
              rows={3}
              helperText="e.g., Initial Contract, Annual Amendment 2025"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSowDialog}>Cancel</Button>
          <Button
            onClick={handleSaveSow}
            variant="contained"
            color="primary"
            disabled={savingSow}
          >
            {savingSow ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete SOW Value"
        message="Are you sure you want to delete this SOW value? This will affect the total SOW calculation."
        details={sowToDelete ? [
          { label: 'Amount', value: `$${sowToDelete.sow_value?.toLocaleString() || 0}` },
          { label: 'Effective Date', value: sowToDelete.effective_from ? format(parseISO(sowToDelete.effective_from), 'MMM dd, yyyy') : '-' },
        ] : []}
      />
    </Container>
  );
};

export default ProjectEdit;
